import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import api from '../api';
import { Send, Bot, User, Loader2, MessageSquare, X, ArrowRight, ImagePlus } from 'lucide-react';
import {
    resolveSession,
    resetVerdict,
    readMessages,
    writeMessages,
    readSessionId,
    writeSessionId,
    patchMeta,
    noteActivity,
    noteLeft,
    clearAll,
} from './chatSessionStore';
import {
    CHAT_IMAGE_ACCEPT,
    useChatImageAttachments,
    imageFilesFrom,
    ChatAttachmentStrip,
    ChatMessageImages,
    ChatDropOverlay,
    modelSupportsImages,
    useImageDrop,
} from './chatImageAttachments';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const applyTemplateVariables = (text, variables = {}) => {
    if (!text) return text;
    let result = text;
    Object.entries(variables).forEach(([key, value]) => {
        if (value != null && String(value).trim()) {
            result = result.split(`{{${key}}}`).join(String(value).trim());
        }
    });
    return result;
};

// Conversation history as the chat API takes it: image ids, not display urls.
const toApiHistory = (messages) => messages.map((msg) => ({
    role: msg.role,
    content: msg.content,
    ...(msg.images?.length ? { images: msg.images.map((img) => img.id) } : {}),
}));

const FORM_SYSTEM_PARAMS = new Set([
    'view', 'token', 'embed_token', 'key', 'theme', 'primary',
    'icon', 'btnColor', 'position',
]);

const ChatEmbedWidget = ({ agentId, embedToken, onClose, themeOverride, fitContentPreChatForm, onFormModeChange, suppressWebhooks = false }) => {
    // Resolved once per mount: whether the conversation this browser has stored
    // is still the visitor's live one (see chatSessionStore). Every piece of
    // state below seeds from it synchronously, so a remount after a host-page
    // navigation paints the existing transcript on first render. Doing it here
    // rather than in an effect also matters for correctness: the greeting effect
    // further down bails out when messages already exist, and it used to read an
    // empty list and overwrite a transcript an effect had only just restored.
    const restoredRef = useRef(null);
    if (restoredRef.current === null) {
        const { resumable, meta } = resolveSession(agentId);
        const messages = resumable ? readMessages(agentId) : [];
        restoredRef.current = {
            resumable,
            meta: resumable ? meta : {},
            messages,
            sessionId: resumable ? readSessionId(agentId) : null,
        };
    }
    const restored = restoredRef.current;

    const [agent, setAgent] = useState(null);
    const [messages, setMessages] = useState(restored.messages);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [greetingLoading, setGreetingLoading] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    // A restored transcript that ends on a system message is a chat that was
    // already closed out (end trigger or inactivity auto-close), so it comes
    // back closed rather than re-opening for input.
    const [sessionClosed, setSessionClosed] = useState(
        Boolean(restored.messages[restored.messages.length - 1]?.isSystemMessage)
    );
    const [reminderSent, setReminderSent] = useState(false);
    const [pendingEndChat, setPendingEndChat] = useState(null); // user message that triggered end-chat detection
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const inputRef = useRef(null);
    const fileInputRef = useRef(null);
    // The session is opened on the first upload if need be, so an image-only
    // first message is still counted against its conversation's image cap.
    const attachments = useChatImageAttachments(
        agentId,
        () => sessionIdRef.current || startNewSession(),
        agent?.chat_image_limits?.per_message,
    );
    const reminderTimerRef = useRef(null);
    const autoCloseTimerRef = useRef(null);
    const pendingEndTimerRef = useRef(null);
    const [themeSettings, setThemeSettings] = useState({
        mode: 'light',
        primaryColor: '#6366f1',
        buttonColor: '#6366f1',
        bgColor: null,
        surfaceColor: null,
        userTextColor: null,
    });
    // The pre-chat form is a gate on starting a conversation, not on every page
    // view. A resumed visitor already answered it, so their answers (and the
    // prompt/greeting injections derived from them) come back with the
    // transcript instead of the form being put in front of them again.
    const [formCompleted, setFormCompleted] = useState(Boolean(restored.meta.formCompleted));
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [formValues, setFormValues] = useState(restored.meta.formVariables || {});
    const [formErrors, setFormErrors] = useState({});
    const formValuesRef = useRef(restored.meta.formVariables || {});
    const formInjectionsRef = useRef(restored.meta.formInjections || null);

    const formFields = useMemo(() => {
        if (!agent?.chat_embed_form_enabled) return [];
        const saved = agent?.chat_embed_form_fields;
        return Array.isArray(saved) && saved.length > 0 ? saved : [];
    }, [agent?.chat_embed_form_enabled, agent?.chat_embed_form_fields]);

    const isFormMode = formFields.length > 0;

    const showPreChatForm = isFormMode && !formCompleted;

    // Let the parent (e.g. the floating launcher window) know when the compact
    // pre-chat form is showing so it can size its container to fit instead of
    // reserving the full chat-window height.
    useEffect(() => {
        if (onFormModeChange) onFormModeChange(showPreChatForm);
    }, [showPreChatForm, onFormModeChange]);

    const getFormVariables = useCallback(() => formValuesRef.current || {}, []);
    // Agent editor Chat Test "Don't send webhooks" toggle. Read through a ref so
    // the memoized send/reminder callbacks always see the current value.
    const suppressWebhooksRef = useRef(suppressWebhooks);
    useEffect(() => { suppressWebhooksRef.current = suppressWebhooks; }, [suppressWebhooks]);

    const validateForm = useCallback(() => {
        const errors = {};
        formFields.forEach((field) => {
            const value = (formValues[field.name] || '').trim();
            if (field.required !== false && !value) {
                errors[field.name] = `${field.label || field.name} is required`;
                return;
            }
            if (value && field.type === 'email' && !EMAIL_REGEX.test(value)) {
                errors[field.name] = 'Enter a valid email address';
            }
        });
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    }, [formFields, formValues]);

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm() || !agent?.id || formSubmitting) return;
        const normalized = {};
        formFields.forEach((field) => {
            const value = (formValues[field.name] || '').trim();
            if (value) normalized[field.name] = value;
        });
        setFormSubmitting(true);
        setError(null);
        try {
            const sid = sessionIdRef.current || startNewSession();
            const response = await api.post(
                `/agents/${agent.id}/chat/embed/form-submitted`,
                {
                    variables: normalized,
                    session_id: sid,
                    ...(suppressWebhooksRef.current ? { suppress_webhooks: true } : {}),
                }
            );
            if (response.data?.session_id) {
                setSessionId(response.data.session_id);
                sessionIdRef.current = response.data.session_id;
                writeSessionId(agentId, response.data.session_id);
            }
            formInjectionsRef.current = response.data || null;
            formValuesRef.current = normalized;
            setFormCompleted(true);
            setFormErrors({});
            // Persist the answers so a host-page navigation resumes straight into
            // the conversation instead of re-asking for them.
            patchMeta(agentId, {
                formCompleted: true,
                formVariables: normalized,
                formInjections: response.data || null,
                lastActivityAt: Date.now(),
                leftAt: null,
            });
        } catch (err) {
            console.error('Failed to submit pre-chat form:', err);
            setError(err.response?.data?.detail || 'Failed to submit form. Please try again.');
        } finally {
            setFormSubmitting(false);
        }
    };

    const handleFormSkip = () => {
        formValuesRef.current = {};
        formInjectionsRef.current = null;
        setFormCompleted(true);
        setFormErrors({});
        patchMeta(agentId, {
            formCompleted: true,
            formVariables: {},
            formInjections: null,
            lastActivityAt: Date.now(),
            leftAt: null,
        });
    };

    const updateFormValue = (name, value) => {
        setFormValues((prev) => ({ ...prev, [name]: value }));
        if (formErrors[name]) {
            setFormErrors((prev) => {
                const next = { ...prev };
                delete next[name];
                return next;
            });
        }
    };

    // Pre-fill form values from URL query params
    useEffect(() => {
        if (formFields.length === 0) return;
        const params = new URLSearchParams(window.location.search);
        const initial = {};
        params.forEach((value, key) => {
            if (!FORM_SYSTEM_PARAMS.has(key) && formFields.some((f) => f.name === key)) {
                if (value && !value.startsWith('{{')) initial[key] = value;
            }
        });
        if (Object.keys(initial).length > 0) {
            setFormValues((prev) => ({ ...prev, ...initial }));
        }
    }, [formFields]);

    // Generate a UUID v4
    const generateSessionId = () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = Math.random() * 16 | 0;
            return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
        });
    };

    // Current session ID, seeded from the resume verdict resolved at mount so a
    // remount keeps talking to the same backend session instead of opening a
    // second one for the same conversation.
    const [sessionId, setSessionId] = useState(restored.sessionId);
    const sessionIdRef = useRef(sessionId);
    useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);

    const startNewSession = () => {
        const newId = generateSessionId();
        setSessionId(newId);
        sessionIdRef.current = newId;
        writeSessionId(agentId, newId);
        noteActivity(agentId);
        return newId;
    };

    const clearSession = () => {
        setSessionId(null);
        sessionIdRef.current = null;
        clearAll(agentId);
        resetVerdict(agentId);
        restoredRef.current = { resumable: false, meta: {}, messages: [], sessionId: null };
    };

    // End the current backend session
    const endBackendSession = async (sid, agentIdParam) => {
        if (!sid || !agentIdParam) return;
        try {
            await api.post(`/agents/${agentIdParam}/chat/embed/end?session_id=${sid}`);
        } catch {}
    };

    // The transcript is restored synchronously at mount (see restoredRef above),
    // not in an effect, so there is nothing to load here.

    // Persist the transcript whenever it changes, so the next mount can restore
    // it. The pre-chat form gate still applies: nothing is written until the
    // visitor is past it, or has been past it on an earlier page.
    useEffect(() => {
        if (!agentId || messages.length === 0) return;
        if (isFormMode && !formCompleted) return;
        writeMessages(agentId, messages);
    }, [messages, agentId, isFormMode, formCompleted]);

    // Load agent details and theme settings
    useEffect(() => {
        const loadAgent = async () => {
            try {
                const response = await api.get(`/agents/${agentId}`);
                setAgent(response.data);
                
                // Apply theme settings from agent or URL params
                const params = new URLSearchParams(window.location.search);
                const urlTheme = params.get('theme');
                const urlPrimary = params.get('primary');
                const urlBtnColor = params.get('btnColor');

                setThemeSettings({
                    mode: themeOverride?.mode || urlTheme || response.data.chat_theme_mode || 'light',
                    primaryColor: themeOverride?.primaryColor || (urlPrimary ? `#${urlPrimary}` : response.data.chat_primary_color) || '#6366f1',
                    buttonColor: themeOverride?.buttonColor || (urlBtnColor ? `#${urlBtnColor}` : response.data.chat_button_color) || response.data.chat_primary_color || '#6366f1',
                    bgColor: themeOverride?.bgColor || null,
                    surfaceColor: themeOverride?.surfaceColor || null,
                    userTextColor: themeOverride?.userTextColor || null,
                    hideAvatars: themeOverride?.hideAvatars || false,
                });
            } catch (err) {
                console.error('❌ Failed to load agent:', err);
                setError('Agent not found');
            } finally {
                setLoading(false);
            }
        };

        if (agentId) {
            loadAgent();
        }
    }, [agentId, themeOverride]);

    // Show greeting when widget opens (disabled by default, like voice AI)
    // When dynamic: fetch greeting from API; otherwise use static message
    // Skip until pre-chat form is completed.
    // Every write here goes through a prev.length guard rather than replacing the
    // list outright: on a resumed conversation the transcript is already present,
    // and a dynamic greeting's in-flight request must not land on top of it.
    useEffect(() => {
        if (!agent || loading || showPreChatForm) return;
        if (messages.length > 0) return; // Don't add greeting if there's already chat history
        const enabled = agent.chat_greeting_enabled;
        const template = (agent.chat_greeting_message || '').trim();
        if (!enabled || !template) return;

        const isDynamic = agent.chat_greeting_dynamic;

        if (!isDynamic) {
            const injections = formInjectionsRef.current;
            const greetingText = injections?.greeting_addition
                ? applyTemplateVariables(injections.greeting_addition, getFormVariables())
                : applyTemplateVariables(template, getFormVariables());
            setMessages(prev => (prev.length > 0 ? prev : [{
                role: 'assistant',
                content: greetingText,
                timestamp: new Date()
            }]));
            return;
        }

        let cancelled = false;
        setGreetingLoading(true);
        const fetchGreeting = async () => {
            try {
                const response = await api.post(
                    `/agents/${agent.id}/chat/embed`,
                    {
                        message: '',
                        conversation_history: [],
                        generate_greeting: true,
                        greeting_template: template,
                        variables: getFormVariables(),
                        session_id: sessionIdRef.current || undefined,
                        ...(suppressWebhooksRef.current ? { suppress_webhooks: true } : {}),
                    }
                );
                if (!cancelled) {
                    if (response.data?.session_id) {
                        setSessionId(response.data.session_id);
                        sessionIdRef.current = response.data.session_id;
                        writeSessionId(agentId, response.data.session_id);
                    }
                    const text = (response.data?.response || '').trim()
                        || applyTemplateVariables(template, getFormVariables());
                    setMessages(prev => (prev.length > 0 ? prev : [{
                        role: 'assistant',
                        content: text,
                        timestamp: new Date()
                    }]));
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Failed to fetch dynamic greeting:', err);
                    const injections = formInjectionsRef.current;
                    const fallback = injections?.greeting_addition
                        ? applyTemplateVariables(injections.greeting_addition, getFormVariables())
                        : applyTemplateVariables(template, getFormVariables());
                    setMessages(prev => (prev.length > 0 ? prev : [{
                        role: 'assistant',
                        content: fallback,
                        timestamp: new Date()
                    }]));
                }
            } finally {
                if (!cancelled) setGreetingLoading(false);
            }
        };
        fetchGreeting();
        return () => { cancelled = true; setGreetingLoading(false); };
    }, [agentId, agent?.id, loading, agent?.chat_greeting_enabled, agent?.chat_greeting_message, agent?.chat_greeting_dynamic, messages.length, showPreChatForm, formCompleted, getFormVariables]);

    // --- Inactivity Reminder & Auto-Close ---

    const messagesRef = useRef(messages);
    useEffect(() => { messagesRef.current = messages; }, [messages]);

    const clearReminderTimer = useCallback(() => {
        if (reminderTimerRef.current) {
            clearTimeout(reminderTimerRef.current);
            reminderTimerRef.current = null;
        }
    }, []);

    const clearAutoCloseTimer = useCallback(() => {
        if (autoCloseTimerRef.current) {
            clearTimeout(autoCloseTimerRef.current);
            autoCloseTimerRef.current = null;
        }
    }, []);

    const clearPendingEndTimer = useCallback(() => {
        if (pendingEndTimerRef.current) {
            clearTimeout(pendingEndTimerRef.current);
            pendingEndTimerRef.current = null;
        }
    }, []);

    const clearInactivityTimers = useCallback(() => {
        clearReminderTimer();
        clearAutoCloseTimer();
        clearPendingEndTimer();
    }, [clearReminderTimer, clearAutoCloseTimer, clearPendingEndTimer]);

    // Use a ref for agent so timer callbacks always see the latest config
    // without needing to be recreated
    const agentRef = useRef(agent);
    useEffect(() => { agentRef.current = agent; }, [agent]);

    const sessionClosedRef = useRef(sessionClosed);
    useEffect(() => { sessionClosedRef.current = sessionClosed; }, [sessionClosed]);

    // Ends the chat from the end-trigger confirmation. Called both when the user presses
    // "Yes, end chat" and when the confirmation is left unanswered until it times out,
    // so both paths post the same message and close the same backend session.
    const endChatFromConfirmation = useCallback(() => {
        if (sessionClosedRef.current) return;
        const ag = agentRef.current;
        const closeMsg = (ag?.chat_end_close_message || '').trim() ||
            'This chat has ended. Feel free to start a new conversation anytime!';
        setMessages(prev => [...prev, {
            role: 'assistant',
            content: closeMsg,
            timestamp: new Date(),
            isSystemMessage: true,
        }]);
        clearInactivityTimers();
        setSessionClosed(true);
        setPendingEndChat(null);
        const sid = sessionIdRef.current;
        if (sid && ag?.id) endBackendSession(sid, ag.id);
    }, [clearInactivityTimers]);

    const startAutoCloseTimer = useCallback(() => {
        const currentAgent = agentRef.current;
        if (!currentAgent?.chat_auto_close_enabled || sessionClosedRef.current) return;
        const seconds = currentAgent.chat_auto_close_seconds ?? 30;

        clearAutoCloseTimer();
        autoCloseTimerRef.current = setTimeout(() => {
            if (sessionClosedRef.current) return;
            const ag = agentRef.current;
            const closeMsg = (ag?.chat_auto_close_message || '').trim() ||
                'This chat session has been closed due to inactivity.';
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: closeMsg,
                timestamp: new Date(),
                isSystemMessage: true,
            }]);
            setSessionClosed(true);
            // End backend session on auto-close
            const sid = sessionIdRef.current;
            if (sid && ag?.id) endBackendSession(sid, ag.id);
        }, seconds * 1000);
    }, [clearAutoCloseTimer]);

    const sendReminder = useCallback(async () => {
        const currentAgent = agentRef.current;
        if (!currentAgent || sessionClosedRef.current) return;

        const type = currentAgent.chat_reminder_type || 'static';
        const messageOrPrompt = (currentAgent.chat_reminder_message || '').trim();

        if (type === 'prompt' && messageOrPrompt) {
            try {
                const conversationHistory = toApiHistory(messagesRef.current);
                const response = await api.post(
                    `/agents/${currentAgent.id}/chat/embed`,
                    {
                        message: messageOrPrompt,
                        conversation_history: conversationHistory,
                        is_reminder: true,
                        variables: getFormVariables(),
                        ...(suppressWebhooksRef.current ? { suppress_webhooks: true } : {}),
                    }
                );
                const reminderText = (response.data?.response || '').trim() || 'Are you still there?';
                setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: reminderText,
                    timestamp: new Date(),
                }]);
            } catch (err) {
                console.error('Failed to generate reminder via prompt:', err);
                setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: messageOrPrompt || 'Are you still there?',
                    timestamp: new Date(),
                }]);
            }
        } else {
            const staticMsg = messageOrPrompt || 'Are you still there? Let me know if you need any help!';
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: staticMsg,
                timestamp: new Date(),
            }]);
        }

        setReminderSent(true);
        startAutoCloseTimer();
    }, [startAutoCloseTimer]);

    const startReminderTimer = useCallback(() => {
        const currentAgent = agentRef.current;
        if (!currentAgent?.chat_reminder_enabled || sessionClosedRef.current) return;
        clearReminderTimer();

        const seconds = currentAgent.chat_reminder_seconds ?? 30;
        reminderTimerRef.current = setTimeout(() => {
            sendReminder();
        }, seconds * 1000);
    }, [clearReminderTimer, sendReminder]);

    // Start inactivity timer whenever the conversation is idle and waiting on the user.
    // That is usually right after an assistant reply, but it also covers a dismissed
    // end-chat confirmation, where the last message in the stream is the user's own
    // trigger phrase and no reply is on the way (isLoading is false).
    // Skip if a reminder was already sent (auto-close timer is running instead) and while
    // an end-chat confirmation is pending — that prompt runs its own timer below.
    useEffect(() => {
        if (!agent?.chat_reminder_enabled || sessionClosed || loading || isLoading || messages.length === 0 || reminderSent || pendingEndChat) return;

        const lastMessage = messages[messages.length - 1];
        if (lastMessage.role === 'assistant' && lastMessage.isSystemMessage) return;
        startReminderTimer();

        // Only cancel the reminder timer on cleanup — never the auto-close timer
        return () => clearReminderTimer();
    }, [messages, agent?.chat_reminder_enabled, sessionClosed, loading, isLoading, reminderSent, pendingEndChat, startReminderTimer, clearReminderTimer]);

    // An unanswered end-chat confirmation must not hold the session open forever. The
    // confirmation prompt is itself the "are you still there?" nudge, so skip the reminder
    // step and treat continued silence as an implicit "yes, end chat".
    useEffect(() => {
        if (!pendingEndChat || sessionClosed) return;
        if (!agent?.chat_reminder_enabled || !agent?.chat_auto_close_enabled) return;

        const seconds = agent.chat_auto_close_seconds ?? 30;
        clearPendingEndTimer();
        pendingEndTimerRef.current = setTimeout(() => {
            endChatFromConfirmation();
        }, seconds * 1000);

        return () => clearPendingEndTimer();
    }, [
        pendingEndChat,
        sessionClosed,
        agent?.chat_reminder_enabled,
        agent?.chat_auto_close_enabled,
        agent?.chat_auto_close_seconds,
        clearPendingEndTimer,
        endChatFromConfirmation,
    ]);

    // Cleanup all timers on unmount
    useEffect(() => {
        return () => clearInactivityTimers();
    }, [clearInactivityTimers]);

    // Record when the page goes away, rather than ending the backend session.
    //
    // This widget lives in an iframe on the customer's site, and from in here a
    // click through to another page of that site is indistinguishable from the
    // visitor closing the tab - both simply unload the frame. Ending the session
    // on unload therefore killed the conversation on every single page change:
    // the next page opened a fresh session, and the visitor's transcript was
    // gone. So we only stamp the time here. The next mount compares that stamp
    // against the resume window and either continues the conversation or ends
    // the abandoned session for real (see resolveSession in chatSessionStore).
    // A visitor who never comes back is closed out by the backend's stale-chat
    // sweep instead.
    //
    // pagehide covers the mobile-Safari case where beforeunload never fires.
    useEffect(() => {
        if (!agentId) return;
        const handleLeave = () => noteLeft(agentId);
        window.addEventListener('pagehide', handleLeave);
        window.addEventListener('beforeunload', handleLeave);
        return () => {
            window.removeEventListener('pagehide', handleLeave);
            window.removeEventListener('beforeunload', handleLeave);
        };
    }, [agentId]);

    // --- End Inactivity Reminder & Auto-Close ---

    // Auto-scroll to bottom when new messages are added (scroll container only, never parent page)
    useEffect(() => {
        const el = messagesContainerRef.current;
        if (el) {
            el.scrollTop = el.scrollHeight;
        }
    }, [messages]);

    // Focus input on mount (preventScroll avoids scrolling parent page when embedded)
    useEffect(() => {
        if (inputRef.current && !loading) {
            inputRef.current.focus({ preventScroll: true });
        }
    }, [loading]);

    const DEFAULT_END_TRIGGERS = ['bye', 'goodbye', 'see you', 'take care', 'later'];

    // Visitors can attach images when the agent allows it (Chat widget settings).
    const imagesEnabled = Boolean(agent?.chat_image_upload_enabled) && modelSupportsImages(agent?.llm_model);

    const handlePaste = (e) => {
        if (!imagesEnabled) return;
        const files = imageFilesFrom(e.clipboardData);
        if (files.length) {
            e.preventDefault();
            attachments.addFiles(files);
        }
    };

    const { dragActive, dropHandlers } = useImageDrop(imagesEnabled && !sessionClosed, attachments.addFiles);

    const matchesEndTrigger = useCallback((text) => {
        if (!agent?.chat_end_enabled) return false;
        let triggers = DEFAULT_END_TRIGGERS;
        if (agent.chat_end_triggers) {
            try { triggers = JSON.parse(agent.chat_end_triggers); } catch {}
        }
        const lower = text.toLowerCase().trim();
        return triggers.some(phrase => {
            const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            return new RegExp(`\\b${escaped}\\b`, 'i').test(lower);
        });
    }, [agent?.chat_end_enabled, agent?.chat_end_triggers]);

    const handleConfirmEndChat = () => {
        endChatFromConfirmation();
    };

    const handleDismissEndChat = () => {
        setPendingEndChat(null);
        // Don't send the trigger phrase to the AI — just dismiss and wait for the user's next
        // message. Clearing pendingEndChat re-arms the inactivity timer, so an abandoned chat
        // still gets the reminder and auto-close treatment after "No, continue".
        setTimeout(() => { if (inputRef.current) inputRef.current.focus({ preventScroll: true }); }, 0);
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();

        const hasImages = imagesEnabled && attachments.readyCount > 0;
        if ((!inputMessage.trim() && !hasImages) || attachments.uploading || isLoading || !agent?.id || sessionClosed) {
            return;
        }

        // User responded – reset inactivity state
        clearInactivityTimers();
        setReminderSent(false);
        // Keeps the conversation inside the resume window, so a visitor who is
        // actively chatting never has it expired out from under them.
        noteActivity(agentId);

        const userMessage = inputMessage.trim();

        // Check if the message matches a chat-end trigger phrase. A message
        // carrying images is never a goodbye, whatever its caption says.
        if (!hasImages && matchesEndTrigger(userMessage)) {
            setInputMessage('');
            // Show the user's message in chat before asking for confirmation
            setMessages(prev => [...prev, { role: 'user', content: userMessage, timestamp: new Date() }]);
            setPendingEndChat(userMessage);
            return;
        }

        // If a confirmation was pending and user sent a different message, dismiss it
        if (pendingEndChat) {
            setPendingEndChat(null);
        }
        setInputMessage('');
        setError(null);

        // Focus input immediately after clearing (preventScroll avoids scrolling parent page)
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus({ preventScroll: true });
            }
        }, 0);

        // Add user message to UI immediately
        const images = hasImages ? attachments.take() : [];
        const newUserMessage = {
            role: 'user',
            content: userMessage,
            ...(images.length ? { images } : {}),
            timestamp: new Date()
        };
        setMessages(prev => [...prev, newUserMessage]);
        setIsLoading(true);

        try {
            // Build conversation history for API
            const conversationHistory = toApiHistory(messages);

            // Ensure we have a session ID for tracking
            const currentSessionId = sessionIdRef.current || startNewSession();

            // Call public embed chat API (no authentication required)
            const response = await api.post(
                `/agents/${agent.id}/chat/embed`,
                {
                    message: userMessage,
                    ...(images.length ? { images: images.map((img) => img.id) } : {}),
                    conversation_history: conversationHistory,
                    session_id: currentSessionId,
                    variables: getFormVariables(),
                    ...(suppressWebhooksRef.current ? { suppress_webhooks: true } : {}),
                }
            );

            // Update session ID from response (backend echoes it back). The
            // backend refuses to resurrect an ended session and hands back a new
            // id instead, so this is also how the widget recovers if the session
            // it resumed had already been finalized server-side.
            if (response.data.session_id && response.data.session_id !== currentSessionId) {
                setSessionId(response.data.session_id);
                sessionIdRef.current = response.data.session_id;
                writeSessionId(agentId, response.data.session_id);
            }

            // Add assistant response
            const assistantMessage = {
                role: 'assistant',
                content: response.data.response,
                timestamp: new Date()
            };
            setMessages(prev => [...prev, assistantMessage]);
        } catch (err) {
            console.error('Chat error:', err);
            setError(err.response?.data?.detail || 'Failed to get response from agent');
            
            // Remove the user message on error so user can retry, with its text
            // and images back in the composer instead of lost.
            setMessages(prev => prev.slice(0, -1));
            setInputMessage(current => current || userMessage);
            attachments.restore(images);
        } finally {
            setIsLoading(false);
            // Focus input again after loading completes (preventScroll avoids scrolling parent page)
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus({ preventScroll: true });
                }
            }, 0);
        }
    };

    const handleClearChat = () => {
        clearInactivityTimers();
        // End the current backend session before starting fresh
        const currentSid = sessionIdRef.current;
        if (currentSid && agent?.id) {
            endBackendSession(currentSid, agent.id);
        }
        clearSession();
        setMessages([]);
        attachments.take();
        setError(null);
        setSessionClosed(false);
        setReminderSent(false);
        if (isFormMode) {
            setFormCompleted(false);
            setFormValues({});
            setFormErrors({});
            formValuesRef.current = {};
            formInjectionsRef.current = null;
        }
        if (inputRef.current) {
            inputRef.current.focus({ preventScroll: true });
        }
    };

    if (loading && !(isFormMode && !formCompleted)) {
        // Use the accent color/mode already known from the launcher (themeOverride)
        // rather than bg-background/text-primary, which follow the global admin
        // dark/light toggle and can flash a dark screen unrelated to this agent's theme.
        const loaderMode = themeOverride?.mode || 'light';
        const loaderAccent = themeOverride?.primaryColor || themeOverride?.buttonColor || '#6366f1';
        const loaderBg = loaderMode === 'dark' ? '#1f2937' : '#ffffff';
        return (
            <div className="flex items-center justify-center h-full p-6 rounded-lg" style={{ backgroundColor: loaderBg }}>
                <div className="text-center">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" style={{ color: loaderAccent }} />
                    <p className="text-sm" style={{ color: loaderAccent, opacity: 0.7 }}>Loading agent...</p>
                </div>
            </div>
        );
    }

    if (error && !agent) {
        return (
            <div className="flex items-center justify-center h-full p-6 bg-background rounded-lg">
                <div className="text-center">
                    <p className="text-destructive mb-2">{error}</p>
                    <p className="text-xs text-muted-foreground">Please check the agent ID.</p>
                </div>
            </div>
        );
    }

    // Apply theme class to the widget
    const isDarkMode = themeSettings.mode === 'dark' || (themeSettings.mode === 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const themeClass = isDarkMode ? 'dark' : 'light';

    // Themed scrollbar for the scrollable areas (pre-chat form + messages).
    // Matches the widget's button color so the default browser scrollbar
    // never clashes with the theme. Scoped by a per-agent class so multiple
    // embedded widgets with different colors don't overwrite each other.
    const scrollClass = `bcx-chat-scroll-${String(agentId || 'x').replace(/[^a-zA-Z0-9_-]/g, '')}`;
    const scrollThumb = themeSettings.buttonColor || '#6366f1';
    const isHex6 = /^#[0-9a-fA-F]{6}$/.test(scrollThumb);
    const thumbIdle = isHex6 ? `${scrollThumb}B3` : scrollThumb; // ~70% opacity
    const trackColor = isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
    const scrollbarCss = `
        .${scrollClass} { scrollbar-width: thin; scrollbar-color: ${thumbIdle} transparent; }
        .${scrollClass}::-webkit-scrollbar { width: 8px; height: 8px; }
        .${scrollClass}::-webkit-scrollbar-track { background: ${trackColor}; border-radius: 9999px; margin: 4px 0; }
        .${scrollClass}::-webkit-scrollbar-thumb { background-color: ${thumbIdle}; border-radius: 9999px; border: 2px solid transparent; background-clip: padding-box; }
        .${scrollClass}::-webkit-scrollbar-thumb:hover { background-color: ${scrollThumb}; }
        .${scrollClass}::-webkit-scrollbar-corner { background: transparent; }
    `;

    if (showPreChatForm) {
        return (
            <div className={`flex flex-col ${fitContentPreChatForm ? '' : 'h-full'} min-h-0 overflow-hidden ${themeClass}`} style={{
                '--primary-color': themeSettings.primaryColor,
                backgroundColor: themeSettings.bgColor || (isDarkMode ? '#1f2937' : '#ffffff'),
                color: isDarkMode ? '#f9fafb' : '#111827'
            }}>
                <style>{scrollbarCss}</style>
                <div
                    className={`${scrollClass} ${fitContentPreChatForm ? 'max-h-[75vh] overflow-y-auto overscroll-contain p-5' : 'flex-1 min-h-0 overflow-y-auto overscroll-contain p-5'}`}
                    style={{ WebkitOverflowScrolling: 'touch' }}
                >
                    <div className="max-w-sm mx-auto w-full space-y-4 pb-2">
                        <div className="text-center space-y-1 pt-1">
                            <div
                                className="w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-2 overflow-hidden"
                                style={agent?.chat_button_logo_url ? undefined : { backgroundColor: themeSettings.primaryColor }}
                            >
                                {agent?.chat_button_logo_url ? (
                                    <img src={agent.chat_button_logo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                ) : (
                                    <MessageSquare className="w-5 h-5 text-white" />
                                )}
                            </div>
                            <h3 className="text-base font-semibold" style={{ color: isDarkMode ? '#f9fafb' : '#111827' }}>
                                {agent?.chat_embed_form_title || 'Before we chat'}
                            </h3>
                            {(agent?.chat_embed_form_subtitle || '').trim() && (
                                <p className="text-xs" style={{ color: isDarkMode ? '#9ca3af' : '#6b7280' }}>
                                    {agent.chat_embed_form_subtitle}
                                </p>
                            )}
                        </div>
                        <form onSubmit={handleFormSubmit} className="space-y-3 pb-1">
                            {formFields.map((field) => (
                                <div key={field.name} className="space-y-1">
                                    <label className="text-xs font-medium block" style={{ color: isDarkMode ? '#d1d5db' : '#374151' }}>
                                        {field.label || field.name}
                                        {field.required !== false && <span className="text-red-500 ml-0.5">*</span>}
                                    </label>
                                    <input
                                        type={field.type === 'email' ? 'email' : field.type === 'tel' ? 'tel' : 'text'}
                                        value={formValues[field.name] || ''}
                                        onChange={(e) => updateFormValue(field.name, e.target.value)}
                                        placeholder={field.placeholder || `Enter ${(field.label || field.name).toLowerCase()}`}
                                        className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2"
                                        style={{
                                            backgroundColor: isDarkMode ? '#111827' : '#ffffff',
                                            border: `1px solid ${formErrors[field.name] ? '#ef4444' : (isDarkMode ? '#374151' : '#e5e7eb')}`,
                                            color: isDarkMode ? '#f9fafb' : '#111827',
                                        }}
                                    />
                                    {formErrors[field.name] && (
                                        <p className="text-xs text-red-500">{formErrors[field.name]}</p>
                                    )}
                                </div>
                            ))}
                            {(agent?.chat_embed_form_disclaimer || '').trim() && (
                                <p
                                    className="text-[11px] leading-relaxed"
                                    style={{ color: isDarkMode ? '#9ca3af' : '#6b7280' }}
                                >
                                    {agent.chat_embed_form_disclaimer.trim()}
                                </p>
                            )}
                            {error && (
                                <p className="text-xs text-red-500">{error}</p>
                            )}
                            <button
                                type="submit"
                                disabled={formSubmitting}
                                className="w-full px-4 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-opacity hover:opacity-90 disabled:opacity-60"
                                style={{ backgroundColor: themeSettings.buttonColor, color: '#ffffff' }}
                            >
                                {formSubmitting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Submitting...
                                    </>
                                ) : (
                                    <>
                                        Start chat
                                        <ArrowRight className="w-4 h-4" />
                                    </>
                                )}
                            </button>
                            {agent?.chat_embed_form_skip_enabled && (
                                <button
                                    type="button"
                                    onClick={handleFormSkip}
                                    className="w-full px-4 py-2 text-sm rounded-lg transition-colors"
                                    style={{
                                        color: isDarkMode ? '#9ca3af' : '#6b7280',
                                        backgroundColor: 'transparent',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = themeSettings.surfaceColor || (isDarkMode ? '#374151' : '#f3f4f6');
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                >
                                    {agent.chat_embed_form_skip_label || 'Skip and chat'}
                                </button>
                            )}
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`flex flex-col h-full min-h-0 overflow-hidden ${themeClass}`} style={{
            '--primary-color': themeSettings.primaryColor,
            backgroundColor: themeSettings.bgColor || (isDarkMode ? '#1f2937' : '#ffffff'),
            color: isDarkMode ? '#f9fafb' : '#111827',
            position: 'relative',
        }} {...dropHandlers}>
            <ChatDropOverlay active={dragActive} accentColor={themeSettings.primaryColor} isDarkMode={isDarkMode} />
            {/* Messages Area - No header when used in button mode (header is in ChatEmbedButton) */}
            {!onClose && (
                <div className="flex items-center justify-between p-4 border-b flex-shrink-0" style={{
                    borderColor: isDarkMode ? '#374151' : '#e5e7eb',
                    backgroundColor: isDarkMode ? '#111827' : '#f9fafb'
                }}>
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0" style={agent?.chat_button_logo_url ? undefined : { backgroundColor: themeSettings.primaryColor }}>
                            {agent?.chat_button_logo_url ? (
                                <img src={agent.chat_button_logo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            ) : (
                                <MessageSquare className="w-4 h-4 text-white" />
                            )}
                        </div>
                        <div>
                            <h3 className="text-sm font-semibold" style={{ color: isDarkMode ? '#f9fafb' : '#111827' }}>{agent?.name || 'Chat'}</h3>
                            <p className="text-xs" style={{ color: isDarkMode ? '#9ca3af' : '#6b7280' }}>AI Assistant</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {messages.length > 0 && (
                            <button
                                onClick={handleClearChat}
                                className="px-2 py-1 text-xs rounded transition-colors"
                                style={{ 
                                    color: isDarkMode ? '#9ca3af' : '#6b7280',
                                    backgroundColor: 'transparent'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = themeSettings.surfaceColor || (isDarkMode ? '#374151' : '#f3f4f6')}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                                End Chat
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Messages Area */}
            <style>{scrollbarCss}</style>
            <div ref={messagesContainerRef} className={`${scrollClass} flex-1 overflow-y-auto p-4 space-y-3 min-h-0`} style={{ backgroundColor: themeSettings.bgColor || (isDarkMode ? '#1f2937' : '#ffffff') }}>
                {messages.length === 0 && !greetingLoading ? (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                        <Bot className="w-12 h-12 opacity-30 mb-3" style={{ color: isDarkMode ? '#9ca3af' : '#6b7280' }} />
                        <p className="text-sm mb-1" style={{ color: isDarkMode ? '#9ca3af' : '#6b7280' }}>Start a conversation</p>
                        <p className="text-xs" style={{ color: isDarkMode ? '#9ca3af' : '#6b7280' }}>Type a message below to chat with the AI</p>
                    </div>
                ) : messages.length === 0 && greetingLoading ? (
                    <div className="flex gap-2 justify-start">
                        <style>{`
                            @keyframes typingDot {
                                0%, 60%, 100% { transform: translateY(0); opacity: 0.7; }
                                30% { transform: translateY(-8px); opacity: 1; }
                            }
                            .greeting-typing-dot { animation: typingDot 1.4s infinite; }
                            .greeting-typing-dot:nth-child(2) { animation-delay: 0.2s; }
                            .greeting-typing-dot:nth-child(3) { animation-delay: 0.4s; }
                        `}</style>
                        {!themeSettings.hideAvatars && (
                            <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: `${themeSettings.primaryColor}20` }}>
                                <Bot className="w-3 h-3" style={{ color: themeSettings.primaryColor }} />
                            </div>
                        )}
                        <div className="rounded-lg px-4 py-3" style={{ backgroundColor: themeSettings.surfaceColor || (isDarkMode ? '#374151' : '#f3f4f6') }}>
                            <div className="flex items-center gap-1">
                                <span className="greeting-typing-dot w-2 h-2 rounded-full block" style={{ backgroundColor: isDarkMode ? '#9ca3af' : '#6b7280' }} />
                                <span className="greeting-typing-dot w-2 h-2 rounded-full block" style={{ backgroundColor: isDarkMode ? '#9ca3af' : '#6b7280' }} />
                                <span className="greeting-typing-dot w-2 h-2 rounded-full block" style={{ backgroundColor: isDarkMode ? '#9ca3af' : '#6b7280' }} />
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                    <style>{`
                        @keyframes messageSlideIn {
                            from {
                                opacity: 0;
                                transform: translateY(10px);
                            }
                            to {
                                opacity: 1;
                                transform: translateY(0);
                            }
                        }
                        .message-animate {
                            animation: messageSlideIn 0.3s ease-out;
                        }
                    `}</style>
                    {messages.map((message, index) => (
                        <div
                            key={index}
                            className={`flex gap-2 message-animate ${
                                message.role === 'user' ? 'justify-end' : 'justify-start'
                            }`}
                        >
                            {message.role === 'assistant' && !themeSettings.hideAvatars && (
                                <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: `${themeSettings.primaryColor}20` }}>
                                    <Bot className="w-3 h-3" style={{ color: themeSettings.primaryColor }} />
                                </div>
                            )}
                            <div
                                className="max-w-[85%] rounded-lg px-3 py-2"
                                style={{
                                    backgroundColor: message.role === 'user' ? themeSettings.primaryColor : (themeSettings.surfaceColor || (isDarkMode ? '#374151' : '#f3f4f6')),
                                    color: message.role === 'user' ? (themeSettings.userTextColor || '#ffffff') : (isDarkMode ? '#f9fafb' : '#111827')
                                }}
                            >
                                <ChatMessageImages images={message.images} />
                                {message.content && (
                                    <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                                )}
                                <p className="text-xs mt-1" style={{
                                    opacity: 0.7,
                                    color: message.role === 'user' ? (themeSettings.userTextColor || '#ffffff') : (isDarkMode ? '#9ca3af' : '#6b7280')
                                }}>
                                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </p>
                            </div>
                            {message.role === 'user' && !themeSettings.hideAvatars && (
                                <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: themeSettings.surfaceColor || (isDarkMode ? '#374151' : '#f3f4f6') }}>
                                    <User className="w-3 h-3" style={{ color: isDarkMode ? '#f9fafb' : '#111827' }} />
                                </div>
                            )}
                        </div>
                    ))
                    }
                    </>
                )}
                {isLoading && (
                    <div className="flex gap-2 justify-start">
                        {!themeSettings.hideAvatars && (
                            <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: `${themeSettings.primaryColor}20` }}>
                                <Bot className="w-3 h-3" style={{ color: themeSettings.primaryColor }} />
                            </div>
                        )}
                        <div className="rounded-lg px-4 py-3" style={{ backgroundColor: themeSettings.surfaceColor || (isDarkMode ? '#374151' : '#f3f4f6') }}>
                            <div className="flex items-center gap-1">
                                <style>{`
                                    @keyframes typingDot {
                                        0%, 60%, 100% {
                                            transform: translateY(0);
                                            opacity: 0.7;
                                        }
                                        30% {
                                            transform: translateY(-8px);
                                            opacity: 1;
                                        }
                                    }
                                    .typing-dot {
                                        animation: typingDot 1.4s infinite;
                                    }
                                    .typing-dot:nth-child(2) {
                                        animation-delay: 0.2s;
                                    }
                                    .typing-dot:nth-child(3) {
                                        animation-delay: 0.4s;
                                    }
                                `}</style>
                                <span 
                                    className="typing-dot w-2 h-2 rounded-full" 
                                    style={{ backgroundColor: isDarkMode ? '#9ca3af' : '#6b7280' }}
                                />
                                <span 
                                    className="typing-dot w-2 h-2 rounded-full" 
                                    style={{ backgroundColor: isDarkMode ? '#9ca3af' : '#6b7280' }}
                                />
                                <span 
                                    className="typing-dot w-2 h-2 rounded-full" 
                                    style={{ backgroundColor: isDarkMode ? '#9ca3af' : '#6b7280' }}
                                />
                            </div>
                        </div>
                    </div>
                )}
                {error && (
                    <div className="rounded-lg p-2" style={{ 
                        backgroundColor: isDarkMode ? '#7f1d1d20' : '#fee2e220',
                        border: `1px solid ${isDarkMode ? '#7f1d1d40' : '#fee2e240'}`
                    }}>
                        <p className="text-xs" style={{ color: '#ef4444' }}>{error}</p>
                    </div>
                )}
                {/* Inline end-chat confirmation — appears in the message stream, non-blocking */}
                {pendingEndChat && (
                    <div className="flex gap-2 justify-start message-animate">
                        {!themeSettings.hideAvatars && (
                            <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: `${themeSettings.primaryColor}20` }}>
                                <Bot className="w-3 h-3" style={{ color: themeSettings.primaryColor }} />
                            </div>
                        )}
                        <div
                            className="max-w-[85%] rounded-lg px-3 py-3"
                            style={{
                                backgroundColor: themeSettings.surfaceColor || (isDarkMode ? '#374151' : '#f3f4f6'),
                                color: isDarkMode ? '#f9fafb' : '#111827',
                            }}
                        >
                            <p className="text-sm mb-3">
                                {(agent?.chat_end_confirm_message || '').trim() || 'Do you want to end the chat?'}
                            </p>
                            <div className="flex gap-2 flex-wrap">
                                <button
                                    type="button"
                                    onClick={handleDismissEndChat}
                                    className="px-3 py-1.5 text-xs rounded-lg transition-colors"
                                    style={{
                                        backgroundColor: isDarkMode ? '#4b5563' : '#e5e7eb',
                                        color: isDarkMode ? '#f9fafb' : '#111827',
                                    }}
                                >
                                    No, continue
                                </button>
                                <button
                                    type="button"
                                    onClick={handleConfirmEndChat}
                                    className="px-3 py-1.5 text-xs rounded-lg transition-colors"
                                    style={{
                                        backgroundColor: themeSettings.buttonColor,
                                        color: '#ffffff',
                                    }}
                                >
                                    Yes, end chat
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="border-t p-3" style={{
                borderColor: themeSettings.surfaceColor || (isDarkMode ? '#374151' : '#e5e7eb'),
                backgroundColor: themeSettings.bgColor || (isDarkMode ? '#111827' : '#f9fafb')
            }}>
                {sessionClosed ? (
                    <div className="flex items-center justify-between gap-2">
                        <p className="text-xs flex-1 text-center" style={{ color: isDarkMode ? '#9ca3af' : '#6b7280' }}>
                            Session closed due to inactivity
                        </p>
                        <button
                            type="button"
                            onClick={handleClearChat}
                            className="px-3 py-1.5 text-xs rounded-lg transition-colors"
                            style={{
                                backgroundColor: themeSettings.buttonColor,
                                color: '#ffffff'
                            }}
                        >
                            New Chat
                        </button>
                    </div>
                ) : (
                    <>
                    {imagesEnabled && (
                        <ChatAttachmentStrip
                            attachments={attachments}
                            isDarkMode={isDarkMode}
                            accentColor={themeSettings.primaryColor}
                        />
                    )}
                    <form onSubmit={handleSendMessage} className="flex gap-2">
                        {imagesEnabled && (
                            <>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept={CHAT_IMAGE_ACCEPT}
                                    multiple
                                    className="hidden"
                                    onChange={(e) => {
                                        attachments.addFiles(e.target.files);
                                        e.target.value = '';
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={isLoading}
                                    aria-label="Attach image"
                                    title="Attach image"
                                    className="px-2 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center flex-shrink-0"
                                    style={{ color: isDarkMode ? '#9ca3af' : '#6b7280', backgroundColor: 'transparent' }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = themeSettings.surfaceColor || (isDarkMode ? '#374151' : '#f3f4f6')}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                    <ImagePlus className="w-4 h-4" />
                                </button>
                            </>
                        )}
                        <input
                            ref={inputRef}
                            type="text"
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e.target.value)}
                            onPaste={handlePaste}
                            placeholder={attachments.items.length ? 'Add a message (optional)...' : 'Type your message...'}
                            disabled={isLoading}
                            className="flex-1 min-w-0 px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2 disabled:opacity-50"
                            style={{
                                backgroundColor: themeSettings.surfaceColor || (isDarkMode ? '#1f2937' : '#ffffff'),
                                border: `1px solid ${themeSettings.surfaceColor ? 'transparent' : (isDarkMode ? '#374151' : '#e5e7eb')}`,
                                color: isDarkMode ? '#f9fafb' : '#111827',
                                focusRing: themeSettings.primaryColor
                            }}
                            onFocus={(e) => e.target.style.boxShadow = `0 0 0 2px ${themeSettings.primaryColor}40`}
                            onBlur={(e) => e.target.style.boxShadow = 'none'}
                        />
                        <button
                            type="submit"
                            disabled={(!inputMessage.trim() && !(imagesEnabled && attachments.readyCount > 0)) || attachments.uploading || isLoading}
                            className="px-3 py-2 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                            style={{
                                backgroundColor: themeSettings.buttonColor,
                                color: '#ffffff'
                            }}
                            onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.opacity = '0.9')}
                            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                        >
                            <Send className="w-4 h-4" />
                        </button>
                    </form>
                    </>
                )}
            </div>
        </div>
    );
};

export default ChatEmbedWidget;

